/* eslint-disable no-mixed-spaces-and-tabs */
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import {
	Box,
	Button,
	Container,
	Divider,
	Grid,
	InputAdornment,
	Link,
	Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "@mui/styles";
import { Field, Form, Formik } from "formik";
import { TextField } from "formik-mui";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import {
	authenticateSSO,
	getCustomLogo,
	loginUser,
	resetLoginErrors,
	setAuthenticated,
	verifySSO,
} from "../../../../export/user";
import LoadingButton from "../../../design/wilya-loading-button";
import { resetPhoneLogin } from "../user-slice";
import Footer from "./../../../components/Footer";
import PhoneLogin from "./phone-login";
import { evaluateSsoCallback, recordSsoAttempt } from "./sso-diagnostics";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const logo: string = require("../../../assets/logo_wilya_dark_plain.svg").default;

const DEFAULT_LOGIN_METHOD = "VERIFY_SSO";

const VERIFY_SSO = "VERIFY_SSO";
const STANDARD_LOGIN = "STANDARD_LOGIN";
const SSO_LOGIN = "SSO_LOGIN";

const DO_NOT_SHOW_LOCKOUT_WARNING_THRESHOLD = 1;

// Mobile-number (texted code) sign-in is only offered where the API enables
// it (the demo environment); everywhere else the login page is unchanged.
const isPhoneLoginEnabled = () =>
	process.env.REACT_APP_PHONE_LOGIN_ENABLED === "true";

const useStyles = makeStyles(() => ({
	paper: {
		marginTop: "96px",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
	},
	form: {
		width: "100%", // Fix IE 11 issue.
		marginTop: "8px",
	},
	submit: {
		marginTop: "16px",
	},
	logo: {
		marginBottom: "16px",
	},
	btnLabel: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "center",
		alignItems: "center",
	},
	progressIndicator: {
		position: "absolute",
		left: "50%",
	},
	errorMessage: {
		color: "#ff1744",
	},
}));

export default function Login() {
	const classes = useStyles();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();

	const ua = navigator.userAgent;
	const touchPoints = navigator.maxTouchPoints || 0;
	const isIOS =
		/iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && touchPoints > 1);
	const isAndroid =
		/android/i.test(ua) ||
		(touchPoints > 0 && !isIOS && !/windows|macintosh|cros/i.test(ua));
	const isMobile = isIOS || isAndroid;

	const queryParams = new URLSearchParams(location.search);
	const hasSsoCode = queryParams.has("code");

	const [loginProcess, setLoginProcess] = useState<string>(DEFAULT_LOGIN_METHOD);
	const [username, setUsername] = useState<string>(
		localStorage.getItem("login_username") ?? ""
	);
	const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
	// When the SSO callback lands on a mobile browser, show a dedicated hand-off
	// screen with an "Open app" button (the automatic scheme launch / OS popup is
	// unreliable on some devices) instead of the web login form.
	const [mobileSsoHandoff, setMobileSsoHandoff] = useState<boolean>(
		isMobile && hasSsoCode
	);
	// Captured callback query (?code&state…) so the hand-off buttons keep working
	// even if the URL is later stripped (e.g. by the SSO-error effect).
	const [ssoCallbackSearch, setSsoCallbackSearch] = useState<string>(
		isMobile && hasSsoCode ? location.search : ""
	);

	const [isSSORedirecting, setIsSSORedirecting] = useState<boolean>(false);
	// Set when this page load is the return leg of an SSO attempt that came back
	// without a usable authorization code (see sso-diagnostics.ts).
	const [ssoDiagnostic, setSsoDiagnostic] =
		useState<ReturnType<typeof evaluateSsoCallback>>(null);
	const [detailsCopied, setDetailsCopied] = useState<boolean>(false);
	const [isPhoneLogin, setIsPhoneLogin] = useState<boolean>(false);

	/* START - Redux States */

	const loginResponse = useSelector((state: any) => state.user.login);
	const status = loginResponse.status;
	const errorMessage = loginResponse.errorMessage;
	const maxFailedAttempts = loginResponse.maxFailedAttempts;
	const failedAttempt = loginResponse.failedAttempt;
	const isLocked = loginResponse.isLocked;
	const accountLockoutEnabled = maxFailedAttempts > 0;

	const customizedBrandingEnabled = useSelector(
		(state: any) => state.user.userData.customizedBrandingEnabled
	);
	const getCustomLogoStatus = useSelector(
		(state: any) => state.user.getCustomLogo.status
	);
	const customLogo = useSelector((state: any) => state.user.customLogo);

	const verifySSOStatus = useSelector(
		(state: any) => state.user.verifySSO.status
	);
	const verifySSOErrorMessage = useSelector(
		(state: any) => state.user.verifySSO.errorMessage
	);

	const isSSOEnabled = useSelector(
		(state: any) => state.user.verifySSO.isSSOEnabled
	);
	const ssoLoginUrl = useSelector(
		(state: any) => state.user.verifySSO.ssoLoginUrl
	);

	const authenticateSSOStatus = useSelector(
		(state: any) => state.user.authenticateSSO.status
	);
	const authenticateSSOErrorMessage = useSelector(
		(state: any) => state.user.authenticateSSO.errorMessage
	);

	const authenticated = useSelector((state: any) => state.user.authenticated);
	/* END - Redux States */

	const clearUsername = useCallback(() => {
		localStorage.removeItem("login_username");
	}, []);

	const storeUsername = useCallback((username: string) => {
		localStorage.setItem("login_username", username);
	}, []);

	const getStoredUsername = useCallback(() => {
		const loginUsername = localStorage.getItem("login_username");
		return loginUsername ?? "";
	}, []);

	const resetErrors = useCallback(() => {
		dispatch(resetLoginErrors());
	}, [dispatch]);

	const handleUsePhoneLogin = useCallback(() => {
		dispatch(resetLoginErrors());
		dispatch(resetPhoneLogin());
		setLoginProcess(DEFAULT_LOGIN_METHOD);
		setIsPhoneLogin(true);
	}, [dispatch]);

	const handleUseEmailLogin = useCallback(() => {
		dispatch(resetPhoneLogin());
		setIsPhoneLogin(false);
	}, [dispatch]);

	const handleCopyDiagnostic = useCallback(async () => {
		if (!ssoDiagnostic) return;
		try {
			await navigator.clipboard.writeText(ssoDiagnostic.details);
			setDetailsCopied(true);
		} catch (err) {
			// Clipboard access can be denied by policy; the reference code shown
			// above is enough for support to find the report in Sentry.
		}
	}, [ssoDiagnostic]);

	useEffect(() => {
		setLoginProcess(DEFAULT_LOGIN_METHOD);
		setUsername(getStoredUsername());
		setPasswordVisible(false);
		dispatch(resetLoginErrors());
	}, [dispatch, getStoredUsername]);

	useEffect(() => {
		if (customizedBrandingEnabled) {
			dispatch(getCustomLogo());
		}
	}, [customizedBrandingEnabled, dispatch]);

	useEffect(() => {
		if (verifySSOStatus === "fulfilled") {
			if (isSSOEnabled) {
				setLoginProcess(SSO_LOGIN);
			} else {
				// Change login layout to standard login
				setLoginProcess(STANDARD_LOGIN);
			}
		} else if (verifySSOStatus === "error") {
			clearUsername();
		}
	}, [verifySSOStatus, isSSOEnabled, ssoLoginUrl, username, clearUsername]);

	// Consumes the attempt marker left by recordSsoAttempt and reports to Sentry
	// when the IdP sent the browser back without a code — the case the effect
	// below cannot see, because it returns early and re-renders a blank form.
	useEffect(() => {
		setSsoDiagnostic(evaluateSsoCallback());
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const code = params.get("code"); // Required
		if (!code) return;

		// Mobile supervisor SSO: the mobile app opens SSO in the browser and uses
		// /login as the IdP redirect target (custom schemes can't be registered
		// with the IdP). On a mobile browser, show a dedicated hand-off screen with
		// an "Open app" button — the automatic scheme launch (and the OS "open in
		// app?" popup) is unreliable on some devices. We still fire a best-effort
		// auto-launch; where it works the app opens immediately, otherwise the user
		// taps the button. The app (once open) completes authenticateSSO itself —
		// the web must NOT also authenticate here (the code is single-use) unless
		// the user explicitly chooses "Continue in browser".
		//
		// This runs regardless of `authenticateSSOStatus` (unlike the desktop path
		// below): on a re-login the browser tab is reused and may still hold a prior
		// attempt's "error" state — always show the hand-off so that stale
		// "Login with SSO failed" screen never appears.
		// Detect the device positively as Android/iOS — the only two platforms the
		// app ships on — instead of trusting the UA string alone. "Request desktop
		// site" spoofs the UA to a desktop one, which would otherwise send the flow
		// down the desktop branch and burn the single-use code on the web, so the
		// app never gets the wilya:// handoff. We therefore also read maxTouchPoints:
		//  - iOS: iPad/iPhone "Request Desktop Website" reports a Mac UA; real Macs
		//    have no touchscreen, so a Mac UA + touch is an iOS device.
		//  - Android: "Request desktop site" spoofs to a Linux/X11 desktop UA; a
		//    touch device that is neither iOS nor a known desktop OS (Windows/Mac/
		//    CrOS) is Android. This excludes Chromebooks and Windows tablets, which
		//    the app doesn't support.
		if (isMobile) {
			resetErrors();
			setSsoCallbackSearch(location.search);
			setMobileSsoHandoff(true);
			window.location.href = `wilya://auth-callback${location.search}`;
			return;
		}

		// Desktop: exchange the single-use code once.
		if (authenticateSSOStatus !== "idle") return;
		const ssoData: any = {};
		for (const param of params) {
			// Adding all params
			ssoData[param[0]] = param[1];
		}
		clearUsername();
		setUsername("");
		setLoginProcess(VERIFY_SSO);
		dispatch(authenticateSSO(ssoData));
	}, [
		location.search,
		dispatch,
		authenticateSSOStatus,
		resetErrors,
		clearUsername,
		isMobile,
	]);

	useEffect(() => {
		if (authenticateSSOStatus === "fulfilled") {
			if (customizedBrandingEnabled) {
				dispatch(getCustomLogo());
			}
		} else if (authenticateSSOStatus === "error") {
			navigate(window.location.pathname);
			clearUsername();
			setLoginProcess(VERIFY_SSO);
		}
	}, [authenticateSSOStatus, dispatch, navigate, customizedBrandingEnabled, clearUsername]);

	useEffect(() => {
		try {
			if (
				getCustomLogoStatus === "fulfilled" ||
				getCustomLogoStatus === "error"
			) {
				dispatch(setAuthenticated(true));
			}
		} catch (e) {
			// Do nothing
		}
	}, [customLogo, dispatch, getCustomLogoStatus, navigate]);

	const handleClick = (e: any) => {
		// Generalising the click handler
		if (e.target.innerText === `${t("user_forgot-password")}?`)
			navigate("/forgot-password");
	};

	// Mobile SSO hand-off: re-fire the deep link on demand (reliable path when the
	// automatic launch is blocked), or fall back to logging into the web app.
	const openAppFromHandoff = () => {
		window.location.href = `wilya://auth-callback${ssoCallbackSearch || location.search}`;
	};
	const continueInBrowser = () => {
		const params = new URLSearchParams(ssoCallbackSearch || location.search);
		const ssoData: any = {};
		for (const param of params) ssoData[param[0]] = param[1];
		setMobileSsoHandoff(false);
		clearUsername();
		setUsername("");
		setLoginProcess(VERIFY_SSO);
		dispatch(authenticateSSO(ssoData));
	};

	const isLoading =
		status === "pending" ||
		verifySSOStatus === "pending" ||
		authenticateSSOStatus === "pending" ||
		getCustomLogoStatus === "pending" ||
		isSSORedirecting ||
		(hasSsoCode && authenticateSSOStatus === "idle" && !mobileSsoHandoff) ||
		((status === "fulfilled" || authenticateSSOStatus === "fulfilled") &&
			!authenticated);

	if (isLoading) {
		return (
			<Container maxWidth='xs'>
				<Box
					sx={{
						marginTop: "96px",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						textAlign: "center",
					}}
				>
					<Box mb={5}>
						<img src={logo} alt='' height={90} />
					</Box>
					<CircularProgress />
					<Typography variant='h6' sx={{ mt: 3, fontWeight: 500 }}>
						{isSSORedirecting
							? t("user_redirecting-to-sso")
							: t("user_authenticating")}
					</Typography>
				</Box>
			</Container>
		);
	}

	if (mobileSsoHandoff) {
		return (
			<Container maxWidth='xs'>
				<Box
					sx={{
						marginTop: "96px",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						textAlign: "center",
					}}
				>
					<Box mb={5}>
						<img src={logo} alt='' height={90} />
					</Box>
					<Typography variant='h6' sx={{ fontWeight: 600, mb: 1, color: "#202020" }}>
						{t("user_return-to-app")}
					</Typography>
					<Typography
						variant='body2'
						sx={{ color: "#6a6a6a", mb: 4, maxWidth: 320, lineHeight: 1.5 }}
					>
						{t("user_handoff-instruction")}
					</Typography>
					<Button
						fullWidth
						variant='contained'
						onClick={openAppFromHandoff}
						sx={{ height: 50, borderRadius: "5px", textTransform: "none", fontWeight: 600 }}
					>
						{t("user_open-app")}
					</Button>
					<Button
						variant='text'
						onClick={continueInBrowser}
						sx={{ mt: 2, textTransform: "none", fontWeight: 500 }}
					>
						{t("user_continue-in-browser")}
					</Button>
				</Box>
			</Container>
		);
	}

	return (
		<>
			<Container maxWidth='xs'>
				<Box
					sx={{
						marginTop: "64px",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
					}}
				>
					{/* <Typography component="h1" variant="h4" className={classes.logo}>
          Gig And Take Logo
        </Typography> */}
					<Box mb={5}>
						<img src={logo} alt='' height={90} />
					</Box>
					{/* <Typography component="h2" variant="h5">
          Sign In
        </Typography> */}
					{ssoDiagnostic?.isUserVisible && (
						<Box
							mb={2}
							sx={{
								display: "flex",
								padding: "12px",
								flexDirection: "row",
								width: "100%",
								backgroundColor: "#FBEAEA",
								borderRadius: "4px",
							}}
						>
							<ErrorOutlineOutlinedIcon sx={{ color: "#E44B48" }} />
							<Box sx={{ marginLeft: "12px", marginTop: "2px" }}>
								<Typography
									sx={{
										fontSize: "16px",
										fontFamily: "Roboto",
										fontWeight: 500,
										color: "#541313",
									}}
								>
									{t("user_login-sso-incomplete")}
								</Typography>
								<Typography
									sx={{
										fontFamily: "Roboto",
										fontWeight: 400,
										fontSize: "14px",
										color: "#541313",
									}}
								>
									{t("user_login-sso-incomplete-detail")}
								</Typography>
								<Typography
									sx={{
										fontFamily: "Roboto",
										fontWeight: 500,
										fontSize: "14px",
										color: "#541313",
										marginTop: "8px",
									}}
								>
									{t("user_login-sso-reference", {
										reference: ssoDiagnostic.reference,
									})}
								</Typography>
								<Button
									size='small'
									sx={{
										textTransform: "none",
										paddingLeft: 0,
										color: "#541313",
										fontWeight: 500,
									}}
									onClick={handleCopyDiagnostic}
								>
									{detailsCopied ? t("user_copied") : t("user_copy-details")}
								</Button>
							</Box>
						</Box>
					)}
					{isPhoneLogin ? (
						<PhoneLogin onUseEmail={handleUseEmailLogin} />
					) : (
					<Formik
						key={`login-${loginProcess}`}
						initialValues={{ username: username, password: "" }}
						validationSchema={Yup.object(
							loginProcess === STANDARD_LOGIN
								? {
										password: Yup.string().required(t("user_password-required")),
										username: Yup.string()
											.email(t("user_invalid-email"))
											.required(t("user_email-required")),
								  }
								: {
										username: Yup.string()
											.email(t("user_invalid-email"))
											.required(t("user_email-required")),
								  }
						)}
						onSubmit={(values, { setSubmitting }) => {
							resetErrors();
							if (loginProcess === VERIFY_SSO) {
								// Storing username for future use
								storeUsername(values.username);
								setUsername(values.username);
								// Calling API to verify if user for SSO
								dispatch(verifySSO(values));
								setSubmitting(false);
							} else if (loginProcess === STANDARD_LOGIN) {
								dispatch(loginUser(values));
								setSubmitting(false);
								} else if (loginProcess === SSO_LOGIN) {
								setSubmitting(false);
								setIsSSORedirecting(true);
								// Record the shape of the authorize request so a callback that
								// never delivers a code can be diagnosed after the fact.
								recordSsoAttempt(ssoLoginUrl);
								// Navigate to SSO login URL
								window.location.replace(ssoLoginUrl);
							}
						}}
					>
						{({ isValid, values, setFieldValue }) => (
							<Form className={classes.form}>
								{/* value will be use as placeholder during SSO authentication process*/}
								{/* If server throw any error user can change this email or try again */}
								<Field
									id='username'
									component={TextField}
									placeholder={t("user_email")}
									name='username'
									type='email'
									label={loginProcess === SSO_LOGIN ? "" : t("user_email")}
									fullWidth
									variant='outlined'
									margin='normal'
									value={
										loginProcess === SSO_LOGIN
											? username ?? ""
											: values.username
									}
									onChange={(event: any) => {
										setFieldValue("username", event.target.value);
										resetErrors();
										// Check SSO again if username changed
										// In case SSO enabled after first attempt
										setLoginProcess(DEFAULT_LOGIN_METHOD);
									}}
									InputLabelProps={{
										shrink: true,
									}}
									InputProps={{
										readOnly: loginProcess === SSO_LOGIN,
										style: {
											WebkitBoxShadow: "0 0 0 1000px #FFF inset !important",
										},
										endAdornment:
											values.username.length > 0 ? (
												<InputAdornment position='end'>
													{loginProcess === SSO_LOGIN ? (
														<CheckCircleIcon sx={{ color: "#2E7D32" }} />
													) : (
														<ClearOutlinedIcon
															sx={{
																color: "rgba(0, 0, 0, 0.56)",
																cursor: "pointer",
															}}
															onClick={() => {
																clearUsername();
																setFieldValue("username", "");
																setUsername("");
																resetErrors();
																setLoginProcess(DEFAULT_LOGIN_METHOD);
															}}
														/>
													)}
												</InputAdornment>
											) : null,
									}}
								/>
								{loginProcess === STANDARD_LOGIN && !isLocked && (
									<Box mt={2}>
										<Field
											id='password'
											component={TextField}
											type={passwordVisible ? "text" : "password"}
											autoComplete='on'
											placeholder={t("user_password")}
											label={t("user_password")}
											name='password'
											fullWidth
											variant='outlined'
											margin='normal'
											InputLabelProps={{
												shrink: true,
											}}
											InputProps={{
												style: {
													WebkitBoxShadow: "0 0 0 1000px #FFF inset !important",
												},
												endAdornment: values.password.length > 0 && (
													<InputAdornment
														position='end'
														sx={{ cursor: "pointer" }}
														onClick={() =>
															setPasswordVisible((prevState) => !prevState)
														}
													>
														{passwordVisible ? (
															<VisibilityOutlinedIcon
																sx={{ color: "rgba(0, 0, 0, 0.56)" }}
															/>
														) : (
															<VisibilityOffOutlinedIcon
																sx={{ color: "rgba(0, 0, 0, 0.56)" }}
															/>
														)}
													</InputAdornment>
												),
											}}
										/>
									</Box>
								)}
								{/* START - Error Box */}
								{/* When account is locked */}
								{isLocked && (
									<Box
										sx={{
											display: "flex",
											padding: "12px",
											flexDirection: "row",
											backgroundColor: "#FBEAEA",
											borderRadius: "4px",
										}}
									>
										<LockOutlined sx={{ color: "#E44B48" }} />
										<Box sx={{ marginLeft: "12px", marginTop: "2px" }}>
											<Typography
												sx={{
													fontSize: "16px",
													fontFamily: "Roboto",
													fontWeight: 500,
													color: "#541313",
												}}
											>
												{t("user_account-locked")}
											</Typography>
											<Typography
												sx={{
													fontFamily: "Roboto",
													fontWeight: 400,
													fontSize: "14px",
													color: "#541313",
												}}
											>
												{t("user_account-locked-reach-out")}{" "}
												<Link href='mailto:help@wilya.com' underline='hover'>
													help@wilya.com
												</Link>{" "}
												{t("user_account-locked-unlock")}
											</Typography>
										</Box>
									</Box>
								)}
								{/* When account is active but got error */}
								{!isLocked &&
									(status === "error" ||
										verifySSOStatus === "error" ||
										authenticateSSOStatus === "error") &&
									(errorMessage !== "" ||
										verifySSOErrorMessage !== "" ||
										authenticateSSOErrorMessage !== "") && (
										<Box mt={2.5} key={`error-${loginProcess}`}>
											{/* Normal password or email login failed */}
											{status === "error" &&
												(!accountLockoutEnabled ||
													(accountLockoutEnabled &&
														failedAttempt ===
															DO_NOT_SHOW_LOCKOUT_WARNING_THRESHOLD)) && (
													<Box
														sx={{
															display: "flex",
															padding: "12px",
															flexDirection: "row",
															backgroundColor: "#FBEAEA",
															borderRadius: "4px",
														}}
													>
														<ErrorOutlineOutlinedIcon
															sx={{ color: "#E44B48" }}
														/>
														<Box sx={{ marginLeft: "12px", marginTop: "2px" }}>
															<Typography
																sx={{
																	fontSize: "16px",
																	fontFamily: "Roboto",
																	fontWeight: 500,
																	color: "#541313",
																}}
															>
																{t("user_login-failed")}
															</Typography>
															<Typography
																sx={{
																	fontFamily: "Roboto",
																	fontWeight: 400,
																	fontSize: "14px",
																	color: "#541313",
																}}
															>
																{errorMessage}
															</Typography>
														</Box>
													</Box>
												)}
											{status === "error" &&
												accountLockoutEnabled &&
												failedAttempt >
													DO_NOT_SHOW_LOCKOUT_WARNING_THRESHOLD && (
													<Box
														sx={{
															display: "flex",
															padding: "12px",
															flexDirection: "row",
															backgroundColor: "#FDF0E6",
															borderRadius: "4px",
														}}
													>
														<WarningAmberOutlined sx={{ color: "#ED6C02" }} />
														<Box sx={{ marginLeft: "12px", marginTop: "2px" }}>
															<Typography
																sx={{
																	fontSize: "16px",
																	fontFamily: "Roboto",
																	fontWeight: 500,
																	color: "#5F2B01",
																}}
															>
																{t("user_login-failed")}
															</Typography>
															<Typography
																sx={{
																	fontSize: "16px",
																	fontFamily: "Roboto",
																	fontWeight: 500,
																	color: "#5F2B01",
																}}
															>
																{t("user_account-could-be-locked")}
															</Typography>
															<Typography
																sx={{
																	fontFamily: "Roboto",
																	fontWeight: 400,
																	fontSize: "14px",
																	color: "#541313",
																}}
															>
																{t("user_attempts-remaining", {
																	count: maxFailedAttempts - failedAttempt,
																})}
															</Typography>
															<Typography
																sx={{
																	fontFamily: "Roboto",
																	fontWeight: 400,
																	fontSize: "14px",
																	color: "#541313",
																}}
															>
																<li>{errorMessage}</li>
																<li>
																	{t("user_reset-password-hint", {
																		link: t("user_forgot-password"),
																	})}
																</li>
															</Typography>
														</Box>
													</Box>
												)}
											{verifySSOStatus === "error" && (
												<Box
													sx={{
														display: "flex",
														padding: "12px",
														flexDirection: "row",
														backgroundColor: "#FBEAEA",
														borderRadius: "4px",
													}}
												>
													<ErrorOutlineOutlinedIcon sx={{ color: "#E44B48" }} />
													<Box sx={{ marginLeft: "12px", marginTop: "2px" }}>
														<Typography
															sx={{
																fontSize: "14px",
																fontFamily: "Roboto",
																fontWeight: 500,
																color: "#541313",
															}}
														>
															{t("user_login-failed")}
														</Typography>
														<Typography
															sx={{
																fontFamily: "Roboto",
																fontWeight: 400,
																fontSize: "14px",
																color: "#541313",
															}}
														>
															{verifySSOErrorMessage}
														</Typography>
													</Box>
												</Box>
											)}
											{authenticateSSOStatus === "error" && (
												<Box
													sx={{
														display: "flex",
														padding: "12px",
														flexDirection: "row",
														backgroundColor: "#FBEAEA",
														borderRadius: "4px",
													}}
												>
													<ErrorOutlineOutlinedIcon sx={{ color: "#E44B48" }} />
													<Box sx={{ marginLeft: "12px", marginTop: "2px" }}>
														<Typography
															sx={{
																fontSize: "14px",
																fontFamily: "Roboto",
																fontWeight: 500,
																color: "#541313",
															}}
														>
															{t("user_login-sso-failed")}
														</Typography>
														<Typography
															sx={{
																fontFamily: "Roboto",
																fontWeight: 400,
																fontSize: "14px",
																color: "#541313",
															}}
														>
															{authenticateSSOErrorMessage}
														</Typography>
													</Box>
												</Box>
											)}
										</Box>
									)}
								{/* END - Error Box */}
								<Grid container pt={5} key={`buttons-${loginProcess}`}>
									{loginProcess === SSO_LOGIN && (
										<Grid item xs={6} pr={1.5}>
											<Button
												fullWidth
												variant='outlined'
												color='primary'
												size='large'
												// Fill the equal-height grid cell so this button matches
												// the SSO button when its longer FR/ES label wraps to two lines
												sx={{ height: "100%" }}
												onClick={() => {
													clearUsername();
													resetErrors();
													setUsername("");
													setFieldValue("username", "");
													setLoginProcess(VERIFY_SSO);
												}}
											>
												<span
													style={{
														fontFamily: "Roboto",
														fontWeight: 500,
														fontSize: "15px",
														lineHeight: "26px",
														letterSpacing: "0.46px",
														textTransform: "uppercase",
														color: "#2F4D8B",
													}}
												>
													{t("user_reset")}
												</span>
											</Button>
										</Grid>
									)}
									<Grid
										item
										xs={loginProcess === SSO_LOGIN ? 6 : 12}
										pl={loginProcess === SSO_LOGIN ? 1.5 : 0}
									>
										<LoadingButton
											type='submit'
											fullWidth
											variant='contained'
											color='primary'
											size='large'
											sx={{ height: "100%" }}
											disabled={
												isLocked ||
												!isValid ||
												(loginProcess === VERIFY_SSO
													? values.username === ""
													: false) ||
												(loginProcess === STANDARD_LOGIN
													? values.password === ""
													: false) ||
												isLoading
											}
											loading={isLoading}
											loadingPosition='start'
											loadingIndicator={
												<CircularProgress
													size={16}
													sx={{ color: "rgba(0, 0, 0, 0.38)" }}
												/>
											}
											startIcon={<></>}
										>
											<div
												style={{
													display: "flex",
													flexDirection: "row",
													alignItems: "center",
													justifyContent: "center",
												}}
											>
												<span
													style={{
														fontFamily: "Roboto",
														fontWeight: 500,
														fontSize: "15px",
														lineHeight: "26px",
														letterSpacing: "0.46px",
														textTransform: "uppercase",
													}}
												>
													{isLoading
														? t("user_loading")
														: loginProcess === SSO_LOGIN
														? t("user_login-with-sso")
														: t("user_login")}
												</span>
											</div>
										</LoadingButton>
									</Grid>
								</Grid>
								{(loginProcess === STANDARD_LOGIN || isLocked) && (
									<Grid container mt={5} justifyContent='center'>
										<Grid item>
											<Link
												variant='body2'
												component='button'
												type='button'
												onClick={handleClick}
												sx={{
													fontFamily: "Roboto",
													fontSize: "16px",
													fontWeight: 400,
													color: "#2F4D8B",
												}}
											>
												{`${t("user_forgot-password")}?`}
											</Link>
										</Grid>
									</Grid>
								)}
							</Form>
						)}
					</Formik>
					)}
					{isPhoneLoginEnabled() && !isPhoneLogin && (
						<Box mt={4} width='100%'>
							<Divider>
								<Typography variant='body2' sx={{ color: "#6a6a6a" }}>
									{t("user_login-or")}
								</Typography>
							</Divider>
							<Button
								fullWidth
								variant='outlined'
								color='primary'
								size='large'
								startIcon={<PhoneIphoneOutlinedIcon />}
								onClick={handleUsePhoneLogin}
								sx={{ mt: 3, textTransform: "none", fontWeight: 500 }}
							>
								{t("user_sign-in-with-mobile")}
							</Button>
						</Box>
					)}
				</Box>
			</Container>
			<Footer />
		</>
	);
}
